import { reactive, ref, computed, watch, onMounted, onBeforeMount } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import * as apiAlert from '@/api/alert'
import { getSearchText, getIsFuzzy, getSearchCategoryName, getGASearchClass } from '@/util/search'
import { getSearchTextInputOfSearchUIParam, getIsFuzzyInputOfSearchUIParam } from '@/util/search-ui'
import { getCommonSuggest } from '@/logic/common/CommonSuggest'

/**
 * 検索UIパーツ
 */
export const getGroup = (props, context) => {

  onBeforeMount(() => {
    group1.search_text_input = getSearchTextInputOfSearchUIParam(group1.searchCategory)
    group1.is_fuzzy_input = getIsFuzzyInputOfSearchUIParam(group1.searchCategory)
  })

  onMounted(() => {
    // 初期表示
    group1.init()
  })

  /**
   * 外部からフォームの検索キーワード、曖昧フラグの値を更新する
   *   store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: 検索キーワード, isFuzzy: 曖昧フラグ })
   *   store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: 検索カテゴリ, searchText: 検索キーワード, isFuzzy: 曖昧フラグ })
   *     ↓
   * store.state.searchUI.inputs.search_text_input
   * store.state.searchUI.inputs.is_fuzzy_input
   * の値が更新される
   */
  watch(() => store.state.searchUI.inputs.search_text_input, (newVal, oldVal) => {
    if (typeof oldVal !== 'undefined') {
      group1.search_text_input = newVal ?? ''
    }
  })
  watch(() => store.state.searchUI.inputs.is_fuzzy_input, (newVal, oldVal) => {
    if (typeof oldVal !== 'undefined') {
      group1.is_fuzzy_input = newVal ?? 0
    }
  })

  const group1 = reactive({

    // 呼び出している画面の種類
    // (詳細検索コンポーネント(PCSearchDetial,SPSearchDetail)に渡すパラメータに変換)
    parentName: computed(() => {
      if (props.screenType === 'dashboard') {
        if (store.state.common.device === 'sp') {
          return 'SPDashboard'
        } else {
          return 'PCDashboard'
        }
      } else if (props.screenType === 'menu') {
        if (store.state.common.device === 'sp') {
          return 'SPMenu'
        } else {
          return 'PCMenu'
        }
      } else if (props.screenType === 'qa') {
        if (store.state.common.device === 'sp') {
          return 'SPQAList'
        } else {
          return 'PCQAList'
        }
      } else if (props.screenType === 'book') {
        if (store.state.common.device === 'sp') {
          return 'SPBookList'
        } else {
          return 'PCBookList'
        }
      } else if (props.screenType === 'download') {
        if (store.state.common.device === 'sp') {
          return '' // SPは無い
        } else {
          return 'PCDocumentDownloadsList'
        }
      } else if (props.screenType === 'knowledge') {
        if (store.state.common.device === 'sp') {
          return 'SPKnowledgeList'
        } else {
          return 'PCKnowledgeList'
        }
      } else if (props.screenType === 'note') {
        if (store.state.common.device === 'sp') {
          return 'SPNoteList'
        } else {
          return 'PCNoteList'
        }
      } else {
        return ''
      }
    }),

    // 呼び出している画面の種類
    // (dashboard:ダッシュボード、qa:QA一覧、book:書籍一覧、download:書式DL一覧、knowledge:最新ナレッジ一覧、note:ノート一覧)
    screenType: computed(() => {
      return props.screenType
    }),

    // 検索ボタン＆検索フォームのGA用class
    gaSearchClass: computed(() => {
      return getGASearchClass(props.screenType, group1.searchCategory)
    }),

    // アラート表示フラグ
    isAlertShown: computed(() => props.isAlertShown),
    alert_count_disp: '', // アラート件数(表示用)

    // キーワード入力欄に入力した文字列
    search_text: computed(() => {
      return getSearchText(group1.searchCategory)
    }),
    search_text_input: '', // データバインディング用

    // 表記の揺れを含む：0(曖昧検索),1(一致検索)
    is_fuzzy: computed(() => {
      return getIsFuzzy(group1.searchCategory)
    }),
    is_fuzzy_input: 0, // データバインディング用

    // 検索カテゴリ（選択肢）
    searchCategory: computed({
      get: () => {
        return store.state.search.searchCategory
      },
      set: (searchCategory) => {
        store.dispatch('search/setSearchCategory', searchCategory)
      }
    }),
    // セレクトボックス変更時に値を取り直すように
    changeCategory: () => {
      if (store.state.common.device === 'sp') {
        searchSelect.isFocusInput = false
      }
      // 入力欄の検索テキストと曖昧フラグを設定する
      group1.search_text_input = group1.search_text
      group1.is_fuzzy_input = group1.is_fuzzy
      // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
      store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: group1.search_text, isFuzzy: group1.is_fuzzy })
    },

    // カテゴリ名
    searchCategoryName: computed(() => {
      return getSearchCategoryName(group1.searchCategory)
    }),

    // 詳細検索実行フラグ(false:簡易検索、true:詳細検索)
    // 詳細検索ウィンドウから実行された場合、ページネーション時に詳細検索を実行するか判断するのに使用する
    // 詳細検索ウィンドウで検索実行時にtrueに設定される
    isSearchDetail: computed(() => {
      if (props.screenType === 'qa') {
        // QA一覧画面の場合
        return (group1.searchCategory === 'qa' && store.state.qa.isSearchDetail)
      } else {
        return false
      }
    }),

    /**
     * 初期表示
     */
    init: () => {
      if (group1.isAlertShown) {
        // アラート（ベルマーク）を表示する場合

        // アラート件数取得APIをコール
        const alertParams = {
          is_unread_only: 1
        }
        apiAlert.count(alertParams) 
          .then(response => {
            if (response) {
              const { data } = response
              // const count = data.count ?? 0
              store
                .dispatch('alert/setUnreadCount', data.count)
                .then(() => {
                  if (store.state.alert.unreadCount >= 10) {
                    group1.alert_count_disp = '9+'
                  } else {
                    group1.alert_count_disp = String(store.state.alert.unreadCount)
                  }
                })
            }
          })
      }
    },

    /**
     * 検索
     */
    search: () => {
      
      const searchCategory = group1.searchCategory
      const searchText = group1.search_text_input ?? ''
      const isFuzzy = group1.is_fuzzy_input ?? 0

      // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
      // 同画面で検索の場合用の設定
      store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: searchText, isFuzzy: isFuzzy })
      // 別画面に遷移して検索する場合用の設定
      store.dispatch('searchUI/setSearchInputOfSearchUIParam', { searchCategory: searchCategory, searchText: searchText, isFuzzy: isFuzzy })

      // 呼び出し元の検索処理に渡すパラメータ
      const options = {
        searchCategory: searchCategory, 
        search_text: searchText, 
        is_fuzzy: isFuzzy
      }

      // 同画面でキーワード検索が実行されたか？
      // (1)キーワード入力欄でEnterを押した
      // (2)キーワード入力欄の検索ボタンを押した場合
      // ※他の画面から遷移してきた場合はfalse
      if (props.isSearchExecutedAtSameScreen) {
        options.search_executed = true
      }

      // このウィンドウを閉じる
      context.emit('close')

      // 呼び出し元の検索を実行
      context.emit('search', options)
    },

    /**
     * 詳細検索を実行する
     * 用途：詳細検索ウィンドウの詳細検索実行ボタン押下時
     */
    searchDetail: (options) => {
      if (group1.parentName === 'PCQAList' || group1.parentName === 'SPQAList') {
        // QA一覧画面の場合
        context.emit('searchDetail', options)
      } else {
        // QA一覧画面でない場合
        // QA一覧画面に遷移
        if (store.state.common.device === 'sp') {
          router.push({ name: 'SPQAList' }).catch(() => {})
        } else {
          router.push({ name: 'PCQAList' }).catch(() => {})
        }
      }
    },

    /**
     * 検索条件リセット(SP用コンポーネント(SPSearchUI)から実行される)
     */
    searchResetOptions: () => {

      // 入力欄をクリア
      group1.search_text_input = ''
      group1.is_fuzzy_input = 0
      // 検索UI(PCSearchUI/SPSearchUI)の検索テキストと曖昧フラグを更新する
      store.dispatch('searchUI/setSearchInputOfSearchUI', { searchText: '', isFuzzy: 0 })

      // 呼び出し元の検索条件リセット処理を実行
      context.emit('searchResetOptions')
    }
    
  })

  const suggest = getCommonSuggest(group1)

  const searchSelect = reactive({
    isFocusInput: false,
    currentValue: 0
  })

  return {
    group1,
    suggest,
    searchSelect
  }
}
